/**
 * Session state.
 *
 * Holds the signed-in user, their acting organisation, and the capability list
 * the server computed for them.
 *
 * `capabilities` drives what the UI shows. It is a usability affordance and
 * NOT a security control — the server re-checks every capability on every
 * request. Hiding a button the user cannot use is politeness; it is never what
 * stops them.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setAccessToken, setSessionEndedHandler, ApiError, resumeSession, getAccessToken } from './api';
import { can, type Capability, type Role, type Tier } from '@opencomply/shared';

export interface SessionUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  language: string;
  mustChangePassword: boolean;
  mfaMethod: string;
}

export interface SessionOrganization {
  id: string;
  name: string;
  tier: Tier;
  plan: string;
  role: Role;
}

export interface Session {
  accessToken: string;
  user: SessionUser;
  organization: SessionOrganization;
  organizations: Array<{ id: string; name: string; role: Role; tier: Tier; isExternal: boolean }>;
}

interface AuthContextValue {
  session: Session | null;
  /** True while the initial silent refresh is in flight. */
  loading: boolean;
  setSession: (session: Session | null) => void;
  signOut: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>;
  /** Capability check for UI visibility. See the note above. */
  allows: (capability: Capability) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const setSession = (next: Session | null) => {
    setAccessToken(next?.accessToken ?? null);
    setSessionState(next);
  };

  // On load, try to resume from the refresh cookie. This is what makes a page
  // reload keep you signed in without ever storing a token where a script
  // could read it.
  useEffect(() => {
    setSessionEndedHandler(() => setSessionState(null));

    (async () => {
      try {
        // Through the single-flight guard, never a direct POST: two tabs or a
        // StrictMode double-invoke would otherwise race the rotation and get
        // the session revoked as suspected token reuse.
        const ok = await resumeSession();
        if (!ok) throw new Error('no session');
        const accessToken = getAccessToken();
        if (!accessToken) throw new Error('no session');
        const me = await api.get<Omit<Session, 'accessToken'>>('/auth/me');
        setSessionState({ accessToken, ...me });
      } catch {
        // No valid refresh cookie — the user simply is not signed in.
        setAccessToken(null);
        setSessionState(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const signOut = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Sign-out must succeed locally even if the network call fails.
    }
    setSession(null);
  };

  const switchOrganization = async (organizationId: string) => {
    const next = await api.post<Session>('/auth/switch-organization', { organizationId });
    setSession(next);
  };

  const allows = (capability: Capability): boolean => {
    if (!session) return false;
    return can(
      {
        userId: session.user.id,
        organizationId: session.organization.id,
        role: session.organization.role,
        tier: session.organization.tier,
      },
      capability,
    );
  };

  const value = useMemo<AuthContextValue>(
    () => ({ session, loading, setSession, signOut, switchOrganization, allows }),
    [session, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

export { ApiError };
